import type { PropsWithChildren, RefObject } from "react";
import React, { memo } from "react";
import clsx from "clsx";
import styles from "./SectionWrapper.module.scss";
import { useCarConfigSelector } from "../../../../redux/store";
import type { SectionType } from "../../../../redux/slices/CarConfigNavigationSlice";
import Button from "../../../../../shared-components/css-modules/lexus/atoms/Button";
import a11y from "../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { SPIN_CONTAINER_ID } from "../spin/SpinNavigation";
import { CarConfigLabel } from "../../../../utils/labelUtils";

type SectionWrapperProps = {
    sectionId: SectionType;
    sectionRef?: RefObject<HTMLElement>;
    hasToggle?: boolean;
    useLbxJourney?: boolean;
};

export type SectionWrapperPropagationProps<T extends string = string> = {
    sectionRefs: Map<T, React.RefObject<HTMLElement>>;
};

const SectionWrapper: React.FC<PropsWithChildren<SectionWrapperProps>> = ({
    children,
    sectionRef,
    sectionId,
    hasToggle = false,
    useLbxJourney = false,
}) => {
    const sectionIsActive = useCarConfigSelector((state) => state.carConfigNavigation.activeSection === sectionId);
    const sectionIsPassed = useCarConfigSelector(
        (state) => state.carConfigNavigation.passedSection.includes(sectionId) ?? false,
    ); // Array or Set for passed sections

    const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault(); // Prevent default scrolling

        const targetElement = document.getElementById(SPIN_CONTAINER_ID);

        if (targetElement) {
            targetElement.focus(); // Set focus on target element
        }
    };

    return (
        <section
            data-testid={sectionId}
            id={sectionId}
            ref={sectionRef}
            className={clsx(
                styles[sectionId],
                styles["config-section"],
                sectionIsActive && styles.active,
                sectionIsPassed && styles.passed,
                useLbxJourney && styles.lbx,
                hasToggle && styles.top,
            )}
            data-section={sectionId}
        >
            <div className={styles["config-section-container"]}>
                {children}
                <Button
                    variant="btn-default"
                    href="#spin-container-wrapper"
                    onClick={handleLinkClick}
                    className={clsx(a11y["skip-to-content"], styles.skip)}
                >
                    <CarConfigLabel label="carConfigA11yLinkSkipToSpinContainer" />
                </Button>
            </div>
        </section>
    );
};

export default memo(SectionWrapper);
